/**
 * Enhanced Cart Tariff Surcharge Display & Breakdown
 * Shows base price, tariff amount, and total for each item
 *
 * NOTE: Price Breakdown box is currently DISABLED because checkout
 * already shows tariff details via Magic Fees. To re-enable, set
 * ENABLE_PRICE_BREAKDOWN to true below.
 */

const ENABLE_PRICE_BREAKDOWN = false; // Set to true to re-enable the Price Breakdown box

class CartTariffDisplay {
  constructor() {
    this.cartData = null;
    this.surchargeData = null;
    this.isCalculating = false; // Prevent duplicate calculations
    this.init();
  }

  init() {
    // Skip initialization if price breakdown is disabled
    if (!ENABLE_PRICE_BREAKDOWN) {
      console.log('[Tariff Display] Price Breakdown box is disabled. Set ENABLE_PRICE_BREAKDOWN = true to re-enable.');
      return;
    }

    this.calculateTariffs();

    // Listen for cart updates
    document.addEventListener('shopify:cart:update', () => {
      this.debouncedCalculate();
    });

    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('.quantity__input')) {
        this.debouncedCalculate();
      }
    });

    // Listen for section updates (when cart is modified)
    document.addEventListener('shopify:section:load', () => {
      this.debouncedCalculate();
    });

    // Use MutationObserver to detect when cart items change
    const cartContainer = document.querySelector('#main-cart-items');
    if (cartContainer) {
      const observer = new MutationObserver(() => {
        this.debouncedCalculate();
      });

      observer.observe(cartContainer, {
        childList: true,
        subtree: true
      });
    }

    // Listen for custom tariff cart update event (from add-to-cart handler)
    document.addEventListener('tariff:cart:updated', () => {
      console.log('Tariff cart update event received');
      // Use longer delay to ensure section re-render is complete
      setTimeout(() => {
        this.calculateTariffs();
      }, 500);
    });
  }

  /**
   * Debounce calculations to prevent rapid re-renders from clearing display
   */
  debouncedCalculate() {
    if (this.calculateTimeout) {
      clearTimeout(this.calculateTimeout);
    }
    this.calculateTimeout = setTimeout(() => {
      this.calculateTariffs();
    }, 400);
  }

  /**
   * Extract tariff data from cart items - tries DOM first, falls back to API
   */
  async calculateTariffs() {
    // Prevent duplicate calculations
    if (this.isCalculating) {
      console.log('Tariff calculation already in progress, skipping');
      return;
    }
    this.isCalculating = true;

    try {
      // First try to get data from DOM attributes
      let cartItems = document.querySelectorAll('[data-cart-item-tariff]');

      // Check if DOM has valid data
      let domHasValidData = false;
      if (cartItems.length > 0) {
        // Verify at least one item has actual tariff data
        for (const item of cartItems) {
          const tariff = parseFloat(item.dataset.cartItemTariff) || 0;
          const basePrice = parseFloat(item.dataset.basePrice) || 0;
          if (tariff > 0 && basePrice > 0) {
            domHasValidData = true;
            break;
          }
        }
      }

      // If DOM doesn't have valid data, fetch from API
      if (!domHasValidData) {
        console.log('DOM data not ready, fetching from /cart.js API...');
        const apiData = await this.fetchCartFromAPI();
        if (apiData && apiData.hasAnyTariff) {
          this.surchargeData = apiData;
          this.renderTariffDisplay();
          return;
        }

        // No tariff data from API either - show nothing
        this.surchargeData = {
          surcharges: [],
          totalSurcharge: '0.00',
          totalBase: '0.00',
          totalWithTariff: '0.00',
          hasAnyTariff: false
        };
        this.renderTariffDisplay();
        return;
      }

      // DOM has valid data - process it
      this.processCartItemsFromDOM(cartItems);
    } finally {
      this.isCalculating = false;
    }
  }

  /**
   * Fetch cart data directly from Shopify API
   */
  async fetchCartFromAPI() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const surcharges = [];
      let totalSurcharge = 0;
      let totalBase = 0;
      let totalWithTariff = 0;
      let hasAnyTariff = false;

      for (const item of cart.items) {
        // Skip tariff fee line items
        if (item.properties && item.properties._is_tariff === 'true') {
          continue;
        }

        // Get tariff data from item properties or product metafields
        let tariffPercent = 0;
        let basePrice = 0;
        let tariffSurcharge = 0;
        let spq = 1;

        // Try to get from item properties first
        if (item.properties) {
          tariffPercent = parseFloat(item.properties._tariff_percentage) || 0;
          tariffSurcharge = parseFloat(item.properties._tariff_per_pack) || 0;
        }

        // If we have tariff percent, we need base price from metafields or calculate it
        // The item.price is in cents and includes tariff, so we work backwards
        if (tariffPercent > 0) {
          hasAnyTariff = true;

          // item.price is in cents per unit (the pack price with tariff)
          // item.line_price is total line price in cents
          const pricePerPack = item.price / 100; // Convert cents to dollars

          // If we have tariff surcharge, base = total - surcharge
          if (tariffSurcharge > 0) {
            basePrice = pricePerPack - tariffSurcharge;
          } else {
            // Calculate surcharge from percentage
            // If price includes tariff: price = base + (base * tariff%)
            // Therefore: base = price / (1 + tariff%)
            const tariffDecimal = tariffPercent > 1 ? tariffPercent / 100 : tariffPercent;
            basePrice = pricePerPack / (1 + tariffDecimal);
            tariffSurcharge = pricePerPack - basePrice;
          }

          const quantity = item.quantity;
          const itemBaseTotal = basePrice * quantity;
          const itemSurchargeTotal = tariffSurcharge * quantity;
          const itemTotalWithTariff = itemBaseTotal + itemSurchargeTotal;

          // Handle both decimal (0.25) and percentage (25) formats
          const tariffPercentDisplay = tariffPercent > 1 ? tariffPercent : (tariffPercent * 100);

          surcharges.push({
            productTitle: item.product_title,
            quantity: quantity,
            basePrice: basePrice.toFixed(4),
            basePriceTotal: itemBaseTotal.toFixed(2),
            tariffPercent: tariffPercentDisplay.toFixed(2),
            surcharge: tariffSurcharge.toFixed(4),
            surchargeTotal: itemSurchargeTotal.toFixed(2),
            totalWithTariff: itemTotalWithTariff.toFixed(2),
          });

          totalSurcharge += itemSurchargeTotal;
          totalBase += itemBaseTotal;
          totalWithTariff += itemTotalWithTariff;
        }
      }

      console.log('Fetched tariff data from API:', { surcharges, totalSurcharge, hasAnyTariff });

      return {
        surcharges: surcharges,
        totalSurcharge: totalSurcharge.toFixed(2),
        totalBase: totalBase.toFixed(2),
        totalWithTariff: totalWithTariff.toFixed(2),
        hasAnyTariff: hasAnyTariff
      };
    } catch (error) {
      console.error('Error fetching cart from API:', error);
      return null;
    }
  }

  /**
   * Process cart items from DOM elements
   */
  processCartItemsFromDOM(cartItems) {
    const surcharges = [];
    let totalSurcharge = 0;
    let totalBase = 0;
    let totalWithTariff = 0;
    let hasAnyTariff = false;

    cartItems.forEach((itemElement) => {
      let tariffPercent = parseFloat(itemElement.dataset.cartItemTariff) || 0;
      const productTitle = itemElement.dataset.productTitle || 'Unknown Product';
      const quantity = parseInt(itemElement.dataset.quantity) || 0;
      // Values are already in dollars (not cents)
      const basePrice = parseFloat(itemElement.dataset.basePrice) || 0;
      const tariffSurcharge = parseFloat(itemElement.dataset.tariffSurcharge) || 0;

      // Handle both decimal (0.25) and percentage (25) formats
      // If value is > 1, it's already a percentage
      const tariffPercentDisplay = tariffPercent > 1 ? tariffPercent : (tariffPercent * 100);

      console.log('Processing item from DOM:', {
        title: productTitle,
        tariffPercent,
        tariffPercentDisplay,
        basePrice,
        tariffSurcharge,
        quantity
      });

      if (tariffPercent > 0 && basePrice > 0) {
        hasAnyTariff = true;

        const itemBaseTotal = basePrice * quantity;
        const itemSurchargeTotal = tariffSurcharge * quantity;
        const itemTotalWithTariff = itemBaseTotal + itemSurchargeTotal;

        surcharges.push({
          productTitle: productTitle,
          quantity: quantity,
          basePrice: basePrice.toFixed(4),
          basePriceTotal: itemBaseTotal.toFixed(2),
          tariffPercent: tariffPercentDisplay.toFixed(2),
          surcharge: tariffSurcharge.toFixed(4),
          surchargeTotal: itemSurchargeTotal.toFixed(2),
          totalWithTariff: itemTotalWithTariff.toFixed(2),
        });

        totalSurcharge += itemSurchargeTotal;
        totalBase += itemBaseTotal;
        totalWithTariff += itemTotalWithTariff;
      }
    });

    this.surchargeData = {
      surcharges: surcharges,
      totalSurcharge: totalSurcharge.toFixed(2),
      totalBase: totalBase.toFixed(2),
      totalWithTariff: totalWithTariff.toFixed(2),
      hasAnyTariff: hasAnyTariff
    };

    console.log('Calculated tariff data from DOM:', this.surchargeData);
    this.renderTariffDisplay();
  }

  /**
   * Render tariff display in cart
   */
  renderTariffDisplay() {
    const container = document.getElementById('tariff-breakdown-container');
    
    if (!container) {
      console.warn('Tariff breakdown container not found');
      return;
    }

    // Clear existing content
    container.innerHTML = '';

    if (!this.surchargeData || !this.surchargeData.hasAnyTariff) {
      return;
    }

    container.innerHTML = this.buildHTML();
    this.injectStyles();
  }

  /**
   * Build comprehensive tariff breakdown HTML
   */
  buildHTML() {
    const data = this.surchargeData;

    let html = `
      <div class="tariff-breakdown-section">
        <div class="tariff-breakdown-header">
          <h3>💰 Price Breakdown</h3>
          <p class="tariff-info-text">Some items include tariff surcharges</p>
        </div>
        
        <div class="tariff-items-list">
    `;

    // Individual items
    data.surcharges.forEach((item) => {
      html += `
        <div class="tariff-item-row">
          <div class="tariff-item-details">
            <div class="tariff-item-name">${this.escapeHtml(item.productTitle)}</div>
            <div class="tariff-item-meta">Qty: ${item.quantity}</div>
          </div>
          <div class="tariff-item-pricing">
            <div class="tariff-price-row">
              <span class="tariff-label">Base:</span>
              <span class="tariff-value">$${item.basePriceTotal}</span>
            </div>
            <div class="tariff-price-row tariff-surcharge-row">
              <span class="tariff-label">Tariff (${item.tariffPercent}%):</span>
              <span class="tariff-value tariff-highlight">+$${item.surchargeTotal}</span>
            </div>
            <div class="tariff-price-row tariff-total-row">
              <span class="tariff-label"><strong>Item Total:</strong></span>
              <span class="tariff-value"><strong>$${item.totalWithTariff}</strong></span>
            </div>
          </div>
        </div>
      `;
    });

    html += `
        </div>
        
        <div class="tariff-summary-box">
          <div class="tariff-summary-row">
            <span class="tariff-summary-label">Total Before Tariffs:</span>
            <span class="tariff-summary-value">$${data.totalBase}</span>
          </div>
          <div class="tariff-summary-row tariff-highlight">
            <span class="tariff-summary-label">Total Tariff Charges:</span>
            <span class="tariff-summary-value">+$${data.totalSurcharge}</span>
          </div>
          <div class="tariff-summary-row tariff-grand-total">
            <span class="tariff-summary-label"><strong>Cart Total with Tariffs:</strong></span>
            <span class="tariff-summary-value"><strong>$${data.totalWithTariff}</strong></span>
          </div>
        </div>
        
        <div class="tariff-notice">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
          </svg>
          <span>Tariff charges are included in the checkout total and will appear on your invoice.</span>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Inject comprehensive styles
   */
  injectStyles() {
    if (document.getElementById('tariff-breakdown-styles')) return;

    const style = document.createElement('style');
    style.id = 'tariff-breakdown-styles';
    style.textContent = `
      .tariff-breakdown-section {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 16px;
        margin: 20px 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .tariff-breakdown-header {
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 2px solid #dee2e6;
      }

      .tariff-breakdown-header h3 {
        margin: 0 0 4px 0;
        font-size: 18px;
        font-weight: 600;
        color: #212529;
      }

      .tariff-info-text {
        margin: 0;
        font-size: 13px;
        color: #6c757d;
      }

      .tariff-items-list {
        margin-bottom: 16px;
      }

      .tariff-item-row {
        background: white;
        border: 1px solid #e9ecef;
        border-radius: 6px;
        padding: 12px;
        margin-bottom: 12px;
      }

      .tariff-item-row:last-child {
        margin-bottom: 0;
      }

      .tariff-item-details {
        margin-bottom: 12px;
      }

      .tariff-item-name {
        font-weight: 600;
        font-size: 14px;
        color: #212529;
        margin-bottom: 4px;
      }

      .tariff-item-meta {
        font-size: 12px;
        color: #6c757d;
      }

      .tariff-item-pricing {
        padding-left: 8px;
        border-left: 3px solid #e9ecef;
      }

      .tariff-price-row {
        display: flex;
        justify-content: space-between;
        padding: 4px 0;
        font-size: 13px;
      }

      .tariff-surcharge-row {
        color: #dc3545;
      }

      .tariff-total-row {
        margin-top: 4px;
        padding-top: 8px;
        border-top: 1px solid #e9ecef;
      }

      .tariff-highlight {
        color: #dc3545;
        font-weight: 500;
      }

      .tariff-summary-box {
        background: #fff3cd;
        border: 1px solid #ffc107;
        border-radius: 6px;
        padding: 12px;
        margin-bottom: 12px;
      }

      .tariff-summary-row {
        display: flex;
        justify-content: space-between;
        padding: 6px 0;
        font-size: 14px;
      }

      .tariff-grand-total {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 2px solid #333;
        font-size: 16px;
      }

      .tariff-notice {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 10px;
        background: #e7f3ff;
        border-left: 3px solid #0066cc;
        border-radius: 4px;
        font-size: 12px;
        color: #004080;
      }

      .tariff-notice svg {
        flex-shrink: 0;
        margin-top: 2px;
      }

      @media (max-width: 640px) {
        .tariff-breakdown-section {
          padding: 12px;
          margin: 16px 0;
        }

        .tariff-item-row {
          padding: 10px;
        }

        .tariff-breakdown-header h3 {
          font-size: 16px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartTariffDisplay = new CartTariffDisplay();
  });
} else {
  window.cartTariffDisplay = new CartTariffDisplay();
}